import { Dispatch, SetStateAction } from 'react'
import { WppIconWarning, WppTab, WppTabs, WppTypography } from '@wppopen/components-library-react'
import clsx from 'clsx'
import { ButtonSkeleton } from 'components/Skeletons'
import type { TabsChangeEventDetail } from '@wppopen/components-library'
import type { WppTabsCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { TabsObject } from './tabs-type.d'
import styles from './Tabs.module.scss'

interface TabsProps {
  currentTab: string
  dataKeys: string[] | TabsObject[]
  showIconInTab?: boolean[]
  onSetNewTab: Dispatch<SetStateAction<string>>
  align?: 'center' | 'left' | 'right'
  className?: string
  isInitialising?: boolean
}

export default function Tabs({
  currentTab,
  dataKeys,
  showIconInTab,
  onSetNewTab,
  align = 'center',
  className,
  isInitialising = false,
}: TabsProps) {
  const onTabChange = (e: WppTabsCustomEvent<TabsChangeEventDetail>) => {
    onSetNewTab(e.detail.value)
  }

  if (!dataKeys.length && !isInitialising)
    return (
      <WppTypography tag="p" type="m-body" className={styles.disclaimer}>
        Sorry, there are no tabs available.
      </WppTypography>
    )

  return (
    <div
      className={clsx(
        styles.nav_container,
        { [styles.center]: align === 'center', [styles.left]: align === 'left', [styles.right]: align === 'right' },
        isInitialising && styles.skeleton,
        className,
      )}
      data-testid="tabs-container"
    >
      {isInitialising ? (
        <>
          {/* if the number of tabs is known, render the skeleton for the number of tabs, else use a generic amount defined by the length of a dummy array */}
          {(dataKeys.length ? dataKeys : new Array(3).fill('')).map((_, idx) => (
            <ButtonSkeleton key={idx} />
          ))}
        </>
      ) : (
        <WppTabs value={currentTab} size="s" data-testid="tabs-group" onWppChange={onTabChange}>
          {dataKeys.map((tab, i) =>
            typeof tab === 'string' ? (
              <WppTab key={tab} value={tab} data-testid="tab">
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </WppTab>
            ) : (
              <WppTab key={tab.view} value={tab.view} data-testid="tab">
                {tab.displayText} {tab?.icon === 'warning' && showIconInTab?.[i] ? <WppIconWarning /> : null}
              </WppTab>
            ),
          )}
        </WppTabs>
      )}
    </div>
  )
}
